import { motion } from 'framer-motion'
import { Icon } from '@iconify/react'
import searchFill from '@iconify/icons-eva/search-fill'
// material
import {
  experimentalStyled as styled,
  useTheme,
} from '@material-ui/core/styles'
import {
  Box,
  alpha,
  Container,
  OutlinedInput,
  InputAdornment,
  Link,
  Grid,
  Typography,
  Chip,
  Avatar,
  Stack,
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Divider,
} from '@material-ui/core'
//
import {
  varFadeInUp,
  TextAnimate,
  MotionInView,
  varWrapEnter,
  varFadeInRight,
  varFadeInDown,
  varFadeInLeft,
} from '../../animate'
import {
  Done,
  Email,
  Language,
  Message,
  Print,
  School,
  Wifi,
} from '@material-ui/icons'
import { useLocation, useNavigate, useParams } from 'react-router'
import Markdown from 'src/components/Markdown'
import RelatedCaseStudyCarousal from '../about/RelatedCaseStudyCarousal'
import { EcommerceNewProducts } from 'src/components/_dashboard/general-ecommerce'
import CaseStudyListItems from './CaseStudyListItems'
import { MIconButton } from 'src/components/@material-extend'
import { keyframes } from '@emotion/react'
import Maintenance from 'src/pages/Maintenance'
import { useRef } from 'react'
import HomeContactForm from './HomeContactForm'
import { data, skynicheAIservices } from './data'
import { Helmet } from 'react-helmet'

// ----------------------------------------------------------------------

const APP_BAR_MOBILE = 64
const APP_BAR_DESKTOP = 88
const APP_BAR_DESKTOP_SMALL = 52

const RootStyle = styled(motion.div)(({ theme }) => ({
  marginTop: '128px',
}))

// ----------------------------------------------------------------------

const ToolbarStyle1 = styled(Toolbar)(({ theme }) => ({
  height: APP_BAR_DESKTOP_SMALL,
  position: 'sticky',
  top: 0,
  zIndex: 1000,
  transition: theme.transitions.create(['height', 'background-color'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  backgroundColor: '#019bdc',
  [theme.breakpoints.up('md')]: {
    height: APP_BAR_DESKTOP_SMALL,
  },
}))

// Define the fade-in keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const RootStyle1 = styled(motion.div)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  opacity: 0, // Set initial opacity to 0
  animation: `${fadeIn} 0.5s ease-in forwards`, // Apply the fade-in animation
  height: 450,
  [theme.breakpoints.up('md')]: {
    height: 850,
    padding: 0,
  },
}))

const ContentStyle = styled('div')(({ theme }) => ({
  textAlign: 'center',
  [theme.breakpoints.up('md')]: {
    textAlign: 'left',
    position: 'absolute',
    top: theme.spacing(4.3),
  },
}))

export default function AiServicesDetailsView() {
  const theme = useTheme()
  const navigate = useNavigate()
  const { title2 } = useParams()
  const location = useLocation()
  const contactRef = useRef(null)
  // add (optional) helper

  // Fetch the relevant data from the array if location.state is not available

  const newsData = skynicheAIservices[0].services.find(
    (item) => item.title2 === title2,
  )
  // console.log(newsData)

  // Destructure the news data

  const {
    date,
    imgSrc,
    imgSrc1,
    title,
    title1,
    description,
    description1,
    category,
    subheading,
    points,
    pointsDescription,
    reflectionDescription,
    brandIdentity,
    SEO,
    time,
    designElements,
    conclusion,
    about_skyniche,
    portal,
    contact,
  } = newsData || {}
  // const { details } = newsData
  // console.log(details)

  // --- SEO: derive from data array with fallbacks (ADD) ---

  const { seo = {} } = newsData || {}

  const stripHtml = (s = '') =>
    s
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim()
  const truncate = (s = '', n = 160) =>
    s.length > n ? `${s.slice(0, n - 1).trim()}…` : s

  const seoTitle = seo.title || title1 || 'News — skyniche Technologies'
  const seoDescription =
    seo.description ||
    truncate(
      stripHtml(description) ||
        stripHtml(subheading) ||
        'Leader in custom software development, IT services, and solutions.',
      160,
    )
  const seoKeywords = Array.isArray(seo.keywords)
    ? seo.keywords.join(', ')
    : `${category || 'News'}, skyniche Technologies`

  const canonicalUrl =
    seo.canonical || `${window.location.origin}${location.pathname}`
  const ogImageRaw = seo.image || imgSrc1 || imgSrc
  const ogImage = ogImageRaw
    ? String(ogImageRaw).startsWith('http')
      ? ogImageRaw
      : `${window.location.origin}${ogImageRaw}`
    : undefined

  const robots = seo.noindex ? 'noindex,nofollow' : 'index,follow'
  const ogType = seo.type || 'article'
  const schemaType = seo.schemaType || 'NewsArticle'
  const publishedISO =
    seo.publishedAt || date
      ? new Date(seo.publishedAt || date).toISOString()
      : undefined
  const orgName = 'skyniche Technologies'

  // Handle case when newsData is not found
  if (!newsData) {
    return <Maintenance />
  }

  const scrollToContact = () => {
    if (contactRef.current) {
      contactRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <RootStyle initial="initial" animate="animate" variants={varWrapEnter}>
      <Helmet>
        {/* Basic */}
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        <meta name="robots" content={robots} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type" content={ogType} />
        <meta property="og:site_name" content={orgName} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        {seo?.imageAlt && (
          <meta property="og:image:alt" content={seo.imageAlt} />
        )}
        <meta property="og:locale" content="en_US" />
        {category && <meta property="article:section" content={category} />}
        {publishedISO && (
          <meta property="article:published_time" content={publishedISO} />
        )}
        {publishedISO && (
          <meta property="og:updated_time" content={publishedISO} />
        )}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        {ogImage && <meta name="twitter:image" content={ogImage} />}

        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': schemaType,
            headline: seoTitle,
            description: seoDescription,
            datePublished: publishedISO,
            dateModified: publishedISO,
            author: { '@type': 'Organization', name: orgName },
            publisher: { '@type': 'Organization', name: orgName },
            image: ogImage ? [ogImage] : undefined,
            mainEntityOfPage: canonicalUrl,
            articleSection: category || undefined,
          })}
        </script>
      </Helmet>

      <Grid
        sx={{
          boxShadow: 0,
          height: '60px',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          marginTop: { xs: '-38px', sm: '-38px', md: 0 },
          marginBottom: { xs: '-3px', sm: '-3px', md: 0 },
        }}
      >
        <ToolbarStyle1 disableGutters>
          <Container
            maxWidth="lg"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: { xs: 0, sm: 0, md: -0.5 },
            }}
          >
            <Stack sx={{ display: 'flex', flexDirection: 'column' }}>
              <MotionInView variants={varFadeInUp}>
                <Typography
                  sx={{
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#26efe9',
                    fontFamily:
                      '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial, sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                  }}
                >
                  About AI Development Services
                </Typography>
              </MotionInView>
              <MotionInView variants={varFadeInUp}>
                <Typography
                  sx={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#fff',
                    fontFamily:
                      '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial, sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                  }}
                >
                  {title1}
                </Typography>
              </MotionInView>
            </Stack>

            {imgSrc && (
              <MotionInView variants={varFadeInUp}>
                <Button
                  variant="outlined"
                  sx={{
                    color: '#c9fbfa',
                    border: '2px solid #c9fbfa',
                    fontWeight: '600',
                    borderRadius: 5,
                    fontFamily:
                      '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial, sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                    '&:hover': {
                      border: '2px solid #c9fbfa',
                      color: '#c9fbfa',
                    },
                    backgroundColor: 'transparent',
                  }}
                  startIcon={
                    <Stack
                      className="tabler--message"
                      sx={{ fontSize: '1.4rem' }}
                    />
                  }
                  onClick={scrollToContact}
                >
                  Contact
                </Button>
              </MotionInView>
            )}
          </Container>
        </ToolbarStyle1>
      </Grid>
      {imgSrc ? (
        <RootStyle1
          initial="initial"
          sx={{
            backgroundSize: 'cover',
            backgroundImage: `url(${imgSrc})`,
            backgroundPosition: 'center',
          }}
          animate="animate"
          variants={varWrapEnter}
        >
          <Container
            maxWidth="lg"
            sx={{ position: 'relative', height: '100%' }}
          ></Container>
        </RootStyle1>
      ) : (
        <Maintenance />
      )}
      {imgSrc && (
        <Box
          maxWidth="lg"
          sx={{
            pt: 5,
            pb: 3,
            pl: { xs: 3, sm: 3, md: 6 },
            pr: { xs: 3, sm: 3, md: 6 },
          }}
        >
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              md={12}
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                flexDirection: 'column',
                mb: 2,
                pl: 2,
              }}
            >
              <MotionInView variants={varFadeInDown}>
                <Typography
                  sx={{
                    mb: 2,
                    fontSize: '2.3rem',
                    fontWeight: 400,
                    color: '#019bdc',
                    lineHeight: 1.2,
                    fontFamily:
                      '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                  }}
                >
                  {title1}
                </Typography>
              </MotionInView>
              <Divider sx={{ mb: 1, borderColor: '#cccccc' }} />
              <Stack
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  flexDirection: 'row',
                  mt: -1,
                }}
              >
                <MotionInView variants={varFadeInUp}>
                  <MIconButton
                    onClick={() =>
                      (window.location.href = 'mailto:info@skyniche.com')
                    }
                  >
                    <Stack
                      className="fontisto--email"
                      sx={{ fontSize: '1.5rem' }}
                    />
                  </MIconButton>
                  <MIconButton
                    onClick={() =>
                      window.open(
                        'https://www.facebook.com/skyniche/',
                        '_blank',
                      )
                    }
                  >
                    <Stack
                      className="mingcute--facebook-line"
                      sx={{ fontSize: '1.5rem' }}
                    />
                  </MIconButton>
                  <MIconButton
                    onClick={() =>
                      window.open(
                        'https://www.instagram.com/skyniche_official/',
                        '_blank',
                      )
                    }
                  >
                    <Stack
                      className="ri--instagram-line"
                      sx={{ fontSize: '1.5rem' }}
                    />
                  </MIconButton>
                </MotionInView>
              </Stack>
              <Divider sx={{ mb: 2, borderColor: '#cccccc' }} />
              <MotionInView variants={varFadeInUp}>
                <Typography
                  sx={{
                    fontSize: '25px',
                    fontStyle: 'italic',
                    fontWeight: 500,
                    mb: 2,
                    color: '#019bdc',
                    lineHeight: 1.2,
                    fontFamily:
                      '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                  }}
                >
                  {details[0].title}
                </Typography>
              </MotionInView>
              <MotionInView variants={varFadeInUp}>
                <Typography
                  sx={{
                    fontSize: '20px',
                    fontWeight: 400,
                    mb: 0,
                    color: '#000048',
                    fontFamily:
                      '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                  }}
                  dangerouslySetInnerHTML={{ __html: details[0].introduction1 }}
                ></Typography>
              </MotionInView>

              <MotionInView variants={varFadeInUp}>
                <Typography
                  sx={{
                    mt: 3,
                    fontSize: '20px',
                    fontWeight: 400,
                    mb: 0,
                    color: '#000048',
                    fontFamily:
                      '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                  }}
                  dangerouslySetInnerHTML={{ __html: details[0].introduction2 }}
                ></Typography>
              </MotionInView>

              <MotionInView variants={varFadeInUp}>
                <Typography
                  sx={{
                    mt: 3,
                    fontSize: '20px',
                    fontWeight: 400,
                    mb: 0,
                    color: '#000048',
                    fontFamily:
                      '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                  }}
                  dangerouslySetInnerHTML={{ __html: details[0].introduction3 }}
                ></Typography>
              </MotionInView>
            </Grid>
            {details[0].developmentServices && (
              <Grid
                item
                xs={12}
                md={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  flexDirection: 'column',
                  mb: 2,
                  pl: 2,
                }}
              >
                <MotionInView variants={varFadeInUp}>
                  <Typography
                    sx={{
                      fontSize: '25px',
                      fontStyle: 'italic',
                      fontWeight: 500,
                      mb: 2,
                      color: '#019bdc',
                      lineHeight: 1.2,
                      fontFamily:
                        '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                    }}
                  >
                    Our AI Development Services
                  </Typography>
                </MotionInView>
                <MotionInView variants={varFadeInUp}>
                  <Typography
                    sx={{
                      fontSize: '20px',
                      fontWeight: 400,
                      mb: 2,
                      color: '#000048',
                      fontFamily:
                        '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                    }}
                    dangerouslySetInnerHTML={{
                      __html: details[0].developmentServices,
                    }}
                  ></Typography>
                </MotionInView>
              </Grid>
            )}

            {details[0].capabilities && (
              <Grid
                item
                xs={12}
                md={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  flexDirection: 'column',
                  mb: 2,
                  pl: 2,
                }}
              >
                <MotionInView variants={varFadeInUp}>
                  <Typography
                    sx={{
                      fontSize: '25px',
                      fontStyle: 'italic',
                      fontWeight: 500,
                      mb: 2,
                      color: '#019bdc',
                      lineHeight: 1.2,
                      fontFamily:
                        '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                    }}
                  >
                    {title2 === 'ai-automation-services'
                      ? 'Our AI automation services include'
                      : title2 === 'ai-development-company'
                      ? 'Key capabilities include'
                      : title2 === 'ai-integration-services'
                      ? 'Integration Capabilities'
                      : title2 === 'ai-agents-for-business'
                      ? 'AI agents developed by skyniche can support:​'
                      : ''}
                  </Typography>
                </MotionInView>
              </Grid>
            )}

            {details[0].capabilities && details[0].capabilities.length > 0 && (
              <Grid
                item
                xs={12}
                md={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  flexDirection: 'column',
                  pl: 2,
                }}
              >
                <MotionInView variants={varFadeInUp}>
                  <Typography sx={{ mb: 2, pl: 4 }}>
                    <Box component={'ul'}>
                      {details[0].capabilities.map((point, index) => (
                        <Typography
                          component={'li'}
                          sx={{
                            mb: 1,
                            fontSize: '19px',
                            fontWeight: 400,
                            color: '#000048',
                          }}
                          key={index}
                          dangerouslySetInnerHTML={{ __html: point }}
                        ></Typography>
                      ))}
                    </Box>
                  </Typography>
                </MotionInView>
              </Grid>
            )}
            <MotionInView variants={varFadeInUp}>
              <Typography
                sx={{
                  ml: 3,
                  fontSize: '20px',
                  fontWeight: 400,
                  mb: 2,
                  color: '#000048',
                  fontFamily:
                    '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                }}
                dangerouslySetInnerHTML={{
                  __html: details[0].capabilitiesSubtitle,
                }}
              ></Typography>
            </MotionInView>

            {details[0].whyus && (
              <Grid
                item
                xs={12}
                md={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  flexDirection: 'column',
                  mb: 2,
                  pl: 2,
                }}
              >
                <MotionInView variants={varFadeInUp}>
                  <Typography
                    sx={{
                      fontSize: '25px',
                      fontStyle: 'italic',
                      fontWeight: 500,
                      mb: 2,
                      color: '#019bdc',
                      lineHeight: 1.2,
                      fontFamily:
                        '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                    }}
                  >
                    Why Businesses Choose skyniche
                  </Typography>
                </MotionInView>

                {details[0].whyus && (
                  <MotionInView variants={varFadeInUp}>
                    <Typography
                      sx={{
                        fontSize: '20px',
                        fontWeight: 400,
                        mb: 2,
                        color: '#000048',
                        fontFamily:
                          '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                      }}
                      dangerouslySetInnerHTML={{
                        __html: details[0].whyus,
                      }}
                    />
                  </MotionInView>
                )}
              </Grid>
            )}

            {details[0].focuses && (
              <Grid
                item
                xs={12}
                md={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  flexDirection: 'column',
                  mb: 2,
                  pl: 2,
                }}
              >
                <MotionInView variants={varFadeInUp}>
                  <Typography
                    sx={{
                      fontSize: '25px',
                      fontStyle: 'italic',
                      fontWeight: 500,
                      mb: 2,
                      color: '#019bdc',
                      lineHeight: 1.2,
                      fontFamily:
                        '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                    }}
                  >
                    Our approach focuses on:
                  </Typography>
                </MotionInView>
              </Grid>
            )}

            {details[0].focuses && details[0].focuses.length > 0 && (
              <Grid
                item
                xs={12}
                md={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  flexDirection: 'column',
                  pl: 2,
                }}
              >
                <MotionInView variants={varFadeInUp}>
                  <Typography sx={{ mb: 2, pl: 4 }}>
                    <Box component={'ul'}>
                      {details[0].focuses.map((point, index) => (
                        <Typography
                          component={'li'}
                          sx={{
                            mb: 1,
                            fontSize: '19px',
                            fontWeight: 400,
                            color: '#000048',
                          }}
                          key={index}
                          dangerouslySetInnerHTML={{ __html: point }}
                        ></Typography>
                      ))}
                    </Box>
                  </Typography>
                </MotionInView>
              </Grid>
            )}
          </Grid>
          <Box sx={{ mt: -2 }} ref={contactRef}>
            <HomeContactForm />
          </Box>
        </Box>
      )}
    </RootStyle>
  )
}
